import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Button, DatePicker, Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Navigate } from 'react-router-dom';
import ContentLayout from '../components/ContentLayout';
import TableSection from '../components/TableSection';
import UserActivityLogsTable, {
  userActivityEventTypeLabels,
} from '../components/UserActivityLogsTable';
import { fetchUserActivityLogs } from '../api/usersApi';
import { useAuth } from '../../../context/useAuth';
import { PAGE_SIZE } from '../../../config';
import fixedBottomDatePickerProps from '../../../shared/utils/datePickerPlacement';
import useTableSearch from '../../../shared/hooks/useTableSearch';
import { isAdmin } from '../../../shared/utils/roles';

const eventTypeOptions = Object.entries(userActivityEventTypeLabels).map(
  ([value, label]) => ({ value, label })
);

export default function UserActivityLogsPage() {
  const { user } = useAuth();
  // Jedinstvena pretraga (LBO) - src/config/searchConfig.js.
  const tableSearch = useTableSearch('userActivityLogs');
  const { reset: resetTableSearch, onChange: setTableSearchEntries } =
    tableSearch;
  const { lboSearch: debouncedLboSearch } = tableSearch.params;
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [dateRange, setDateRange] = useState(['', '']);
  const [activityLogs, setActivityLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [message, setMessage] = useState('');
  const latestLogsRequestRef = useRef(0);

  const handleTableSearchChange = useCallback(
    (entries) => {
      setTableSearchEntries(entries);
      setCurrentPage(1);
    },
    [setTableSearchEntries]
  );

  const loadActivityLogs = useCallback(async (page = currentPage) => {
    const requestId = latestLogsRequestRef.current + 1;
    latestLogsRequestRef.current = requestId;
    setListLoading(true);

    const { data, ok, message } = await fetchUserActivityLogs({
      page,
      limit: PAGE_SIZE,
      lbo: debouncedLboSearch,
      eventType: selectedEventTypes,
      from: dateRange[0],
      to: dateRange[1],
    });

    if (requestId !== latestLogsRequestRef.current) {
      return;
    }

    setListLoading(false);

    if (ok && data) {
      const resolvedPage = data?.pagination?.currentPage || 1;

      setActivityLogs(data.activityLogs || []);
      if (resolvedPage !== page) {
        setCurrentPage(resolvedPage);
      }
      setTotalPages(data?.pagination?.totalPages || 1);
      setTotalResults(data?.pagination?.totalCount ?? null);
      return;
    }

    if (message) {
      setMessage(message);
    }
  }, [currentPage, dateRange, debouncedLboSearch, selectedEventTypes]);

  useEffect(() => {
    loadActivityLogs(currentPage);
  }, [loadActivityLogs]);

  const handleEventTypesChange = useCallback((nextTypes) => {
    setSelectedEventTypes(nextTypes);
    setCurrentPage(1);
  }, []);

  const handleDateRangeChange = useCallback((from, to) => {
    setDateRange([from, to]);
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    resetTableSearch();
    setSelectedEventTypes([]);
    setDateRange(['', '']);
    setCurrentPage(1);
  }, [resetTableSearch]);

  const hasActiveFilters = Boolean(
    tableSearch.isActive ||
      selectedEventTypes.length ||
      dateRange[0] ||
      dateRange[1]
  );

  const eventTypeFilter = useMemo(
    () => ({
      type: 'custom',
      wide: true,
      value: selectedEventTypes,
      render: () => (
        <Select
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          options={eventTypeOptions}
          placeholder="Tip aktivnosti"
          value={selectedEventTypes}
          onChange={handleEventTypesChange}
          style={{ width: '100%' }}
        />
      ),
    }),
    [handleEventTypesChange, selectedEventTypes]
  );

  const dateRangeFilter = useMemo(
    () => ({
      type: 'custom',
      wide: true,
      value: dateRange,
      render: () => (
        <DatePicker.RangePicker
          {...fixedBottomDatePickerProps}
          className="search-input"
          allowClear
          allowEmpty={[true, true]}
          format="DD.MM.YYYY"
          placeholder={['Datum od', 'Datum do']}
          value={[
            dateRange[0] ? dayjs(dateRange[0]) : null,
            dateRange[1] ? dayjs(dateRange[1]) : null,
          ]}
          onChange={(dates) =>
            handleDateRangeChange(
              dates?.[0] ? dates[0].format('YYYY-MM-DD') : '',
              dates?.[1] ? dates[1].format('YYYY-MM-DD') : ''
            )
          }
        />
      ),
    }),
    [dateRange, handleDateRangeChange]
  );

  const emptyState = useMemo(
    () => ({
      icon: '/assets/icons/no-users.svg',
      title: 'Trenutno nema korisničkih izmena',
    }),
    []
  );

  if (!isAdmin(user)) {
    return <Navigate to="/neovlascen" replace />;
  }

  return (
    <>
      <ContentLayout>
        <TableSection
          // Za loader tabele (da li jos nema redova).
          data={activityLogs}
          showFilter
          showCreateButton={false}
          searchFieldOrder={[
            'tableSearch',
            'secondarySearch',
            'tertiarySearch',
          ]}
          onSearch={() => {}}
          filter={{ selected: {}, sections: [] }}
          onFilterChange={() => {}}
          tableSearch={{ ...tableSearch, onChange: handleTableSearchChange }}
          secondarySearch={eventTypeFilter}
          tertiarySearch={dateRangeFilter}
          inlineHeaderActions={
            hasActiveFilters ? (
              <Button
                type="default"
                className="btn-reset-filters"
                onClick={handleResetFilters}
              >
                <span className="btn-reset-filters__content">
                  Poništi filtere
                  <CloseOutlined />
                </span>
              </Button>
            ) : null
          }
          customTable={
            <UserActivityLogsTable
              data={activityLogs}
              emptyState={emptyState}
            />
          }
          showPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          paginationLoading={listLoading}
          onPageChange={setCurrentPage}
        />
      </ContentLayout>
      {!!message && <span style={{ display: 'none' }}>{message}</span>}
    </>
  );
}
